// Dominio puro — comprobante fiscal recibido de un proveedor (Wave 4).
// Valida el bloque antes de mandarlo al comando y da labels para la UI.
// Tipos: tabla "Comprobantes Compras" del Libro IVA Digital (RG 3685).

import type { ExpenseFiscalInvoiceInput, ExpenseRow } from './expenseTypes';
import { parseDecimalInput } from '../../shared/numeric/localeNumeric';

export type PurchaseDocKind = 'factura' | 'nota_debito' | 'nota_credito';
export type PurchaseDocClass = 'A' | 'B' | 'C' | 'M';

export type PurchaseDocTypeOption = {
  code: number;
  docClass: PurchaseDocClass;
  kind: PurchaseDocKind;
  /** El comprobante discrimina IVA → captura desglose. B/C no discriminan. */
  discriminatesIva: boolean;
  label: string;
};

export const PURCHASE_DOC_TYPES: PurchaseDocTypeOption[] = [
  { code: 1, docClass: 'A', kind: 'factura', discriminatesIva: true, label: 'Factura A' },
  { code: 2, docClass: 'A', kind: 'nota_debito', discriminatesIva: true, label: 'Nota de débito A' },
  { code: 3, docClass: 'A', kind: 'nota_credito', discriminatesIva: true, label: 'Nota de crédito A' },
  { code: 6, docClass: 'B', kind: 'factura', discriminatesIva: false, label: 'Factura B' },
  { code: 7, docClass: 'B', kind: 'nota_debito', discriminatesIva: false, label: 'Nota de débito B' },
  { code: 8, docClass: 'B', kind: 'nota_credito', discriminatesIva: false, label: 'Nota de crédito B' },
  { code: 11, docClass: 'C', kind: 'factura', discriminatesIva: false, label: 'Factura C' },
  { code: 12, docClass: 'C', kind: 'nota_debito', discriminatesIva: false, label: 'Nota de débito C' },
  { code: 13, docClass: 'C', kind: 'nota_credito', discriminatesIva: false, label: 'Nota de crédito C' },
  { code: 51, docClass: 'M', kind: 'factura', discriminatesIva: true, label: 'Factura M' },
  { code: 52, docClass: 'M', kind: 'nota_debito', discriminatesIva: true, label: 'Nota de débito M' },
  { code: 53, docClass: 'M', kind: 'nota_credito', discriminatesIva: true, label: 'Nota de crédito M' },
];

const DOC_TYPE_BY_CODE = new Map(PURCHASE_DOC_TYPES.map((t) => [t.code, t]));

export const purchaseDocType = (code: number | null | undefined): PurchaseDocTypeOption | null =>
  code == null ? null : DOC_TYPE_BY_CODE.get(code) ?? null;

export const purchaseDocTypeLabel = (code: number | null | undefined): string =>
  purchaseDocType(code)?.label ?? 'Comprobante';

export const docTypeDiscriminatesIva = (code: number | null | undefined): boolean =>
  purchaseDocType(code)?.discriminatesIva ?? false;

export const isCreditNoteDocType = (code: number | null | undefined): boolean =>
  purchaseDocType(code)?.kind === 'nota_credito';

/** Misma clase A/B/C/M que la factura de compra, tipo nota de crédito (Libro IVA). */
export const creditNoteDocTypeFromPurchaseInvoice = (
  invoiceDocType: number | null | undefined,
): number | null => {
  const doc = purchaseDocType(invoiceDocType);
  if (!doc || doc.kind !== 'factura') return null;
  return (
    PURCHASE_DOC_TYPES.find((t) => t.docClass === doc.docClass && t.kind === 'nota_credito')?.code ??
    null
  );
};

/** Label legible de la alícuota AFIP (ids del Libro IVA): 5→'21 %', etc. */
export const ALICUOTA_ID_LABEL: Record<number, string> = {
  3: '0 %',
  4: '10,5 %',
  5: '21 %',
  6: '27 %',
  8: '5 %',
  9: '2,5 %',
};
export const alicuotaIdLabel = (id: number): string => ALICUOTA_ID_LABEL[id] ?? `Alícuota ${id}`;

// ── Draft del formulario → input del comando ────────────────────────────────
// Strings crudos de la UI; la conversión es pura y testeable.

export type ExpenseFiscalAlicuotaDraft = {
  /** Id de alícuota AFIP (3=0%, 4=10,5%, 5=21%, 6=27%, 8=5%, 9=2,5%). */
  rateId: number;
  baseImpText: string;
  ivaText: string;
};

export type ExpenseFiscalDraft = {
  docType: number | null;
  pvText: string;
  numberText: string;
  supplierTaxId: string;
  supplierName: string;
  alicuotas: ExpenseFiscalAlicuotaDraft[];
  opExText: string;        // operaciones exentas
  totConcText: string;     // no gravado
  percIvaText: string;
  percNacionalesText: string;
  percIibbText: string;
  percMunicipalesText: string;
};

export const emptyExpenseFiscalDraft = (): ExpenseFiscalDraft => ({
  docType: null,
  pvText: '',
  numberText: '',
  supplierTaxId: '',
  supplierName: '',
  alicuotas: [{ rateId: 5, baseImpText: '', ivaText: '' }],
  opExText: '',
  totConcText: '',
  percIvaText: '',
  percNacionalesText: '',
  percIibbText: '',
  percMunicipalesText: '',
});

export const expenseFiscalDraftIsEmpty = (d: ExpenseFiscalDraft): boolean =>
  d.docType == null &&
  !d.pvText.trim() && !d.numberText.trim() && !d.supplierTaxId.trim() &&
  !d.supplierName.trim() && !d.opExText.trim() && !d.totConcText.trim() &&
  !d.percIvaText.trim() && !d.percNacionalesText.trim() &&
  !d.percIibbText.trim() && !d.percMunicipalesText.trim() &&
  d.alicuotas.every((a) => !a.baseImpText.trim() && !a.ivaText.trim());

const num = (text: string): number => parseDecimalInput(text, { min: 0 }) ?? 0;

/** Total fiscal del comprobante: neto + IVA + exento + no gravado + percepciones. */
export const expenseFiscalDraftTotal = (d: ExpenseFiscalDraft): number => {
  const iva = d.alicuotas.reduce((s, a) => s + num(a.ivaText), 0);
  return (
    d.alicuotas.reduce((s, a) => s + num(a.baseImpText), 0) +
    iva + num(d.opExText) + num(d.totConcText) +
    num(d.percIvaText) + num(d.percNacionalesText) + num(d.percIibbText) + num(d.percMunicipalesText)
  );
};

/**
 * Draft → input del comando. `null` cuando la sección quedó vacía (sin tipo)
 * → gasto informal, no entra al libro.
 * El desglose solo viaja en docs que discriminan (A/M); en B/C se ignora.
 */
const moneyText = (n: number | null | undefined): string =>
  n == null || !Number.isFinite(n) || n === 0 ? '' : String(n);

/** ExpenseRow → draft para editar / completar factura tardía. */
export const expenseFiscalRowToDraft = (
  row: Pick<
    ExpenseRow,
    | 'invoiceDocType'
    | 'invoicePv'
    | 'invoiceNumber'
    | 'supplierTaxId'
    | 'invoiceSupplierName'
    | 'invoiceIvaBreakdown'
    | 'invoicePerceptions'
  >,
): ExpenseFiscalDraft => {
  const breakdown = row.invoiceIvaBreakdown;
  const alicuotas =
    breakdown?.alicuotas?.map((a) => ({
      rateId: a.id,
      baseImpText: moneyText(a.baseImp),
      ivaText: moneyText(a.importe),
    })) ?? [{ rateId: 5, baseImpText: '', ivaText: '' }];
  const perc = row.invoicePerceptions;
  return {
    docType: row.invoiceDocType ?? null,
    pvText: row.invoicePv != null ? String(row.invoicePv) : '',
    numberText: row.invoiceNumber != null ? String(row.invoiceNumber) : '',
    supplierTaxId: row.supplierTaxId ?? '',
    supplierName: row.invoiceSupplierName ?? '',
    alicuotas: alicuotas.length > 0 ? alicuotas : [{ rateId: 5, baseImpText: '', ivaText: '' }],
    opExText: moneyText(breakdown?.impOpEx),
    totConcText: moneyText(breakdown?.impTotConc),
    percIvaText: moneyText(perc?.iva),
    percNacionalesText: moneyText(perc?.nacionales),
    percIibbText: moneyText(perc?.iibb),
    percMunicipalesText: moneyText(perc?.municipales),
  };
};

export const expenseFiscalDraftToInput = (
  d: ExpenseFiscalDraft
): ExpenseFiscalInvoiceInput | null => {
  if (d.docType == null) return null;
  const discriminates = docTypeDiscriminatesIva(d.docType);
  const alicuotas = discriminates
    ? d.alicuotas
        .map((a) => ({ id: a.rateId, baseImp: num(a.baseImpText), importe: num(a.ivaText) }))
        .filter((a) => a.baseImp > 0 || a.importe > 0)
    : [];
  const opEx = num(d.opExText);
  const totConc = num(d.totConcText);
  const hasBreakdown =
    discriminates && (alicuotas.length > 0 || opEx > 0 || totConc > 0);
  const perceptions = {
    iva: num(d.percIvaText),
    nacionales: num(d.percNacionalesText),
    iibb: num(d.percIibbText),
    municipales: num(d.percMunicipalesText),
  };
  const hasPerceptions = perceptions.iva + perceptions.nacionales + perceptions.iibb + perceptions.municipales > 0;
  return {
    docType: d.docType,
    pointOfSale: num(d.pvText),
    invoiceNumber: num(d.numberText),
    supplierTaxId: d.supplierTaxId.trim(),
    supplierName: d.supplierName.trim() || null,
    ivaBreakdown: hasBreakdown
      ? {
          alicuotas,
          impNeto: alicuotas.reduce((s, a) => s + a.baseImp, 0),
          impIva: alicuotas.reduce((s, a) => s + a.importe, 0),
          impOpEx: opEx,
          impTotConc: totConc,
        }
      : null,
    perceptions: hasPerceptions ? perceptions : null,
  };
};

/**
 * Valida el bloque fiscal del comprobante recibido.
 * Devuelve mensaje de error o null si está OK. Reglas:
 *  - tipo/PV/número/CUIT obligatorios (espeja el check constraint server-side);
 *  - el desglose IVA solo tiene sentido en docs que discriminan (A/M);
 *  - neto + IVA ≥ 0 y ≥ 0 en percepciones.
 */
export function validateExpenseFiscalInvoice(input: ExpenseFiscalInvoiceInput): string | null {
  const doc = purchaseDocType(input.docType);
  if (!doc) return 'Elegí un tipo de comprobante válido.';
  if (!Number.isInteger(input.pointOfSale) || input.pointOfSale < 1 || input.pointOfSale > 99999) {
    return 'El punto de venta del comprobante debe ser entre 1 y 99999.';
  }
  if (!Number.isInteger(input.invoiceNumber) || input.invoiceNumber < 1) {
    return 'El número del comprobante debe ser mayor a cero.';
  }
  const taxId = (input.supplierTaxId ?? '').replace(/\D/g, '');
  if (taxId.length !== 11 && !(taxId.length >= 7 && taxId.length <= 8)) {
    return 'El CUIT del proveedor debe tener 11 dígitos (o DNI de 7/8).';
  }
  if (!doc.discriminatesIva && input.ivaBreakdown?.alicuotas?.length) {
    return 'Las facturas B/C no discriminan IVA — dejá el desglose vacío.';
  }
  const b = input.ivaBreakdown;
  if (b) {
    for (const a of b.alicuotas ?? []) {
      if (!(a.baseImp > 0)) return 'Cada alícuota necesita un neto mayor a cero.';
      if (a.importe < 0) return 'El IVA de una alícuota no puede ser negativo.';
    }
    for (const v of [b.impNeto, b.impIva, b.impOpEx, b.impTotConc]) {
      if (v != null && v < 0) return 'Los importes del desglose no pueden ser negativos.';
    }
  }
  const p = input.perceptions;
  if (p) {
    for (const v of [p.iva, p.nacionales, p.iibb, p.municipales]) {
      if (v != null && v < 0) return 'Las percepciones no pueden ser negativas.';
    }
  }
  return null;
}
